/**
 * BookingFormModal
 *
 * Multi-step modal for creating or editing a workshop booking.
 *
 * CREATE flow (4 steps):
 *   Step 1 – Customer Lookup    : search by name and/or mobile
 *   Step 2 – Customer & Vehicle : pre-filled (read-only) or blank (editable)
 *   Step 3 – Handover & Pickup  : optional contact details for the people
 *                                 dropping the vehicle off and collecting it
 *                                 (skippable — all fields optional)
 *   Step 4 – Booking Details    : Service Profile / On Demand selector +
 *       On Demand mode  → Date, Time, Service Types (multi, each with own
 *                         duration in d/h/m and per-row description),
 *                         Odometer In (optional)
 *       Service Profile → Date, Time, Profile summary (work items / parts /
 *                         labour / duration totals), Odometer In (optional),
 *                         Description / Notes
 *
 *       When Service Profile mode is selected but no profile is picked
 *       (e.g. the workshop has no profiles set up yet) the booking is
 *       created via the ad-hoc path with an empty serviceTypes array.
 *       The resulting job starts with no work items and the user adds
 *       them later from the Job Detail Modal — either manually or via
 *       the Repco SmartQuote import on the Work Items tab.
 *
 * EDIT flow (3 steps, step 1 skipped):
 *   Step 2 – Customer & Vehicle (read-only)
 *   Step 3 – Handover & Pickup
 *   Step 4 – Booking Details + Loan Car panel + work item mode selector
 */

import React, { useState, useEffect, useRef } from 'react';
import {
    X, Search, Loader2, User, Users, Car, ChevronRight, ChevronLeft, ChevronDown, ChevronUp,
    Phone, CheckCircle, AlertCircle, Clock,
    ClipboardList, Wrench, Package, DollarSign, Gauge, Check, Pencil,
    GripVertical, Calendar
} from 'lucide-react';
import MileageServicePanel from './booking/MileageServicePanel';
import { getActiveServiceProfilesApi, getServiceProfileApi } from '../services/bookingApi';
import LoanCarAllocationPanel from './LoanCarAllocationPanel';
import useEscapeToClose from './useEscapeToClose';
// Steps 1 and 2 are shared with the quote form — see CustomerVehiclePicker.
import { CustomerLookupStep, CustomerVehicleStep } from './customers/CustomerVehiclePicker';
import { buildCustomerPayload, buildVehiclePayload } from '../utils/customerVehicle';

// ==================== Constants ====================

const TIME_SLOTS = [
    '07:00', '08:00', '09:00', '10:00', '11:00', '12:00',
    '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'
];



// ── Formatting helpers ──
const fmt$ = (val) =>
    val != null ? `$${parseFloat(val).toFixed(2)}` : null;

const fmtHours = (hours) => {
    if (hours == null) return null;
    const h = parseFloat(hours);
    if (h === 0) return null;
    const wholeH = Math.floor(h);
    const mins   = Math.round((h - wholeH) * 60);
    if (wholeH === 0) return `${mins}m`;
    return mins > 0 ? `${wholeH}h ${mins}m` : `${wholeH}h`;
};


// ==================== Date Picker Field ====================
//
// Replaces native <input type="date"> in places where the inline browser
// picker feels rigid — on some platforms the popup has no Done/OK button
// so users can't cleanly commit a selection. This wraps a date input in a
// popover with an explicit OK / Cancel pair. Output format is identical
// to the native control ("YYYY-MM-DD"), and it emits a synthetic event
// shaped like { target: { name, value } } so the existing `onChange={onChange}`
// handler in BookingFormModal works without changes.

const DatePickerField = ({ name, value, onChange, required = false, ariaLabel }) => {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState('');
    const wrapRef = useRef(null);

    const formatDisplay = (v) => {
        if (!v) return 'Select date';
        // value is "YYYY-MM-DD"; parse as local date to avoid TZ shift
        const [y, m, d] = v.split('-').map(Number);
        if (!y || !m || !d) return v;
        const dt = new Date(y, m - 1, d);
        if (isNaN(dt.getTime())) return v;
        return dt.toLocaleDateString('en-AU', {
            weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
        });
    };

    const openPicker = () => {
        setDraft(value || '');
        setOpen(true);
    };
    const closePicker = () => setOpen(false);
    const commit = () => {
        if (!draft) return;
        onChange({ target: { name, value: draft } });
        setOpen(false);
    };

    useEffect(() => {
        if (!open) return;
        const onDocClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    return (
        <div className="relative" ref={wrapRef}>
            <button
                type="button"
                onClick={open ? closePicker : openPicker}
                aria-label={ariaLabel || name}
                aria-required={required || undefined}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm bg-white text-left flex items-center justify-between gap-2 hover:border-gray-300 transition-colors"
            >
                <span className={value ? 'text-gray-900' : 'text-gray-400'}>
                    {formatDisplay(value)}
                </span>
                <Calendar size={14} className="text-gray-400 shrink-0" />
            </button>

            {open && (
                <div className="absolute z-50 mt-1 left-0 w-64 bg-white border border-gray-200 rounded-xl shadow-lg p-3 space-y-3">
                    <div>
                        <label className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1">Date</label>
                        <input
                            type="date"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-violet-500"
                            autoFocus
                        />
                    </div>
                    <div className="flex gap-2 pt-1">
                        <button
                            type="button"
                            onClick={closePicker}
                            className="flex-1 py-1.5 text-sm font-medium bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={commit}
                            disabled={!draft}
                            className="flex-1 py-1.5 text-sm font-medium bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 transition-colors"
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// ==================== Duration Input ====================

const DurationInput = ({ value, onChange, compact = false, disabled = false }) => {
    const decompose = (totalMins) => {
        const total = parseInt(totalMins) || 0;
        return {
            days:    Math.floor(total / (60 * 8)),
            hours:   Math.floor((total % (60 * 8)) / 60),
            minutes: total % 60,
        };
    };

    const [parts, setParts] = useState(() => decompose(value));

    useEffect(() => { setParts(decompose(value)); }, [value]);

    const compose = (d, h, m) => d * 8 * 60 + h * 60 + m;

    const handlePartChange = (field, raw) => {
        const parsed  = parseInt(raw);
        const clamped = isNaN(parsed) || parsed < 0 ? 0 : parsed;
        const next    = { ...parts, [field]: clamped };
        if (field === 'hours'   && clamped > 7)  next.hours   = 7;
        if (field === 'minutes' && clamped > 59) next.minutes = 59;
        setParts(next);
        const total = compose(next.days, next.hours, next.minutes);
        onChange(total > 0 ? total : '');
    };

    const spinnerClass = compact
        ? 'w-12 px-1.5 py-1 border border-gray-200 rounded-md focus:ring-1 focus:ring-violet-500 ' +
        'focus:border-violet-500 text-xs text-center [appearance:textfield] ' +
        '[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
        : 'w-16 px-2 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 ' +
        'focus:border-violet-500 text-sm text-center [appearance:textfield] ' +
        '[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';

    // Compact mode: inline d / h / m suffix labels on a single row, no footer.
    if (compact) {
        const InlineUnit = ({ field, max, suffix }) => (
            <div className="flex items-center gap-0.5">
                <input
                    type="number" min="0" max={max}
                    disabled={disabled}
                    value={parts[field] === 0 ? '' : parts[field]}
                    placeholder="0"
                    onChange={(e) => handlePartChange(field, e.target.value)}
                    onBlur={(e) => { if (e.target.value === '') handlePartChange(field, 0); }}
                    className={spinnerClass + (disabled ? ' bg-gray-50 text-gray-500 cursor-not-allowed' : '')}
                />
                <span className="text-[11px] text-gray-500">{suffix}</span>
            </div>
        );
        return (
            <div className="flex items-center gap-1.5">
                <InlineUnit field="days"    max={99} suffix="d" />
                <InlineUnit field="hours"   max={7}  suffix="h" />
                <InlineUnit field="minutes" max={59} suffix="m" />
            </div>
        );
    }

    const Unit = ({ label, field, max }) => (
        <div className="flex flex-col items-center gap-1">
            <input
                type="number" min="0" max={max}
                value={parts[field] === 0 ? '' : parts[field]}
                placeholder="0"
                onChange={(e) => handlePartChange(field, e.target.value)}
                onBlur={(e) => { if (e.target.value === '') handlePartChange(field, 0); }}
                className={spinnerClass}
            />
            <span className="text-xs text-gray-500 font-medium">{label}</span>
        </div>
    );

    const totalMins = compose(parts.days, parts.hours, parts.minutes);

    return (
        <div className="space-y-1.5">
            <div className="flex items-end gap-3">
                <Unit label="Days"  field="days"    max={99} />
                <span className="pb-6 text-gray-400 font-medium text-sm">:</span>
                <Unit label="Hours" field="hours"   max={7}  />
                <span className="pb-6 text-gray-400 font-medium text-sm">:</span>
                <Unit label="Mins"  field="minutes" max={59} />
                {totalMins > 0 && (
                    <span className="pb-6 text-xs text-gray-400 italic">
                        = {totalMins} min{totalMins !== 1 ? 's' : ''}
                    </span>
                )}
            </div>
            <p className="text-xs text-gray-400">1 day = 8 hours</p>
        </div>
    );
};

// ==================== Step Indicator ====================

const StepIndicator = ({ currentStep, isEdit }) => {
    const steps = isEdit
        ? [
            { num: 2, label: 'Customer & Vehicle' },
            { num: 3, label: 'Handover & Pickup' },
            { num: 4, label: 'Booking Details' },
        ]
        : [
            { num: 1, label: 'Find Customer' },
            { num: 2, label: 'Customer & Vehicle' },
            { num: 3, label: 'Handover & Pickup' },
            { num: 4, label: 'Booking Details' },
        ];

    const displaySteps = steps.map((s, i) => ({ ...s, displayIdx: i + 1 }));

    return (
        <div className="flex items-center gap-0 px-4 py-3 border-b border-gray-100 shrink-0">
            {displaySteps.map((step, i) => (
                <React.Fragment key={step.num}>
                    <div className="flex items-center gap-1.5">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-colors
                            ${currentStep === step.num
                            ? 'bg-violet-600 text-white'
                            : currentStep > step.num
                                ? 'bg-emerald-500 text-white'
                                : 'bg-gray-100 text-gray-400'
                        }`}>
                            {currentStep > step.num ? <CheckCircle size={14} /> : step.displayIdx}
                        </div>
                        <span className={`text-xs font-medium hidden sm:inline transition-colors
                            ${currentStep === step.num ? 'text-violet-700' : 'text-gray-400'}`}>
                            {step.label}
                        </span>
                    </div>
                    {i < displaySteps.length - 1 && (
                        <div className={`flex-1 h-px mx-2 transition-colors
                            ${currentStep > step.num ? 'bg-emerald-400' : 'bg-gray-200'}`} />
                    )}
                </React.Fragment>
            ))}
        </div>
    );
};


const ServiceProfileSelector = ({
                                    mode, onModeChange, selectedProfileUuid, onProfileSelect,
                                    vehicleUuid, mileageOdometerKm, onMileageChange,
                                }) => {
    const [profiles, setProfiles]       = useState([]);
    const [loading, setLoading]         = useState(false);
    const [error, setError]             = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const searchRef = useRef(null);

    useEffect(() => {
        if (mode !== 'serviceProfile') return;
        if (profiles.length > 0) return;
        setLoading(true);
        setError(null);
        getActiveServiceProfilesApi()
            .then(page => setProfiles(page?.content ?? page ?? []))
            .catch(() => setError('Could not load service profiles. Please try again.'))
            .finally(() => setLoading(false));
    }, [mode]);

    useEffect(() => {
        if (mode === 'serviceProfile' && !loading && profiles.length > 0) {
            setTimeout(() => searchRef.current?.focus(), 50);
        }
    }, [mode, loading, profiles.length]);

    const filtered = profiles.filter(p => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase();
        return (
            p.name?.toLowerCase().includes(q) ||
            p.make?.toLowerCase().includes(q) ||
            p.model?.toLowerCase().includes(q)
        );
    });

    const ModeButton = ({ value, icon, label, description }) => {
        const active = mode === value;
        return (
            <button type="button" onClick={() => onModeChange(value)}
                    className={`flex-1 flex items-start gap-3 px-3.5 py-3 rounded-xl border-2 text-left transition-all
                    ${active
                        ? 'border-violet-500 bg-violet-50 ring-1 ring-violet-300'
                        : 'border-gray-200 bg-white hover:border-violet-200 hover:bg-violet-50/30'
                    }`}
            >
                <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0
                    ${active ? 'border-violet-500' : 'border-gray-300'}`}>
                    {active && <div className="w-2 h-2 rounded-full bg-violet-500" />}
                </div>
                <div>
                    <div className="flex items-center gap-1.5">
                        {icon}
                        <span className={`text-sm font-semibold ${active ? 'text-violet-800' : 'text-gray-700'}`}>
                            {label}
                        </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 leading-snug">{description}</p>
                </div>
            </button>
        );
    };

    return (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3">
            <div className="flex items-center gap-2">
                <ClipboardList size={17} className="text-violet-600" />
                <h3 className="font-semibold text-gray-900">Work Items</h3>
            </div>

            {/* Mileage first: it is the one that answers "what does this car need"
                without the person taking the booking having to know. The other two
                require them to decide. */}
            {/* ⚠️ Stacked below sm, in a row above it. Two of these fitted side by side
                on a 375px phone; three do not — each would be about 110px wide carrying
                an icon, a label and a two-line description, which wraps into an
                unreadable column. The phone gets one full-width button per mode. */}
            <div className="flex flex-col sm:flex-row gap-2">
                <ModeButton
                    value="mileage"
                    icon={<Gauge size={13} className="text-violet-500" />}
                    label="By Mileage"
                    description="Enter the odometer reading and the services due are worked out from it and the vehicle's history."
                />
                <ModeButton
                    value="serviceProfile"
                    icon={<ClipboardList size={13} className="text-violet-500" />}
                    label="Service Profile"
                    description="Reuse a pre-built profile of work items, parts, costs and measurements set up in Configuration."
                />
                <ModeButton
                    value="onDemand"
                    icon={<Wrench size={13} className="text-gray-500" />}
                    label="On Demand"
                    description="Start with an empty job and add the work items by hand."
                />
            </div>

            {mode === 'mileage' && (
                <MileageServicePanel
                    vehicleUuid={vehicleUuid}
                    odometerKm={mileageOdometerKm}
                    onOdometerChange={onMileageChange}
                />
            )}

            {mode === 'serviceProfile' && (
                <div className="space-y-2.5 pt-1">
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        <input
                            ref={searchRef} type="text" value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search by name, make or model…"
                            className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm"
                        />
                    </div>

                    {loading && (
                        <div className="flex items-center justify-center gap-2 py-6 text-gray-400 text-sm">
                            <Loader2 size={16} className="animate-spin" /> Loading profiles…
                        </div>
                    )}
                    {!loading && error && (
                        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                            <AlertCircle size={15} className="shrink-0" /> {error}
                        </div>
                    )}
                    {!loading && !error && filtered.length === 0 && (
                        searchQuery.trim() ? (
                            <div className="py-5 text-center text-sm text-gray-400">
                                No profiles match "{searchQuery}"
                            </div>
                        ) : (
                            // No active service profiles. Rather than dead-ending the
                            // user we let them continue with no profile selected — the
                            // booking is created with an empty job and work items can
                            // be added later from the Job Detail Modal (including the
                            // Repco SmartQuote import on the Work Items tab).
                            <div className="rounded-lg border border-violet-200 bg-violet-50/60 p-3.5 text-sm text-violet-800 space-y-1">
                                <div className="flex items-center gap-2 font-medium">
                                    <ClipboardList size={14} /> No service profiles yet
                                </div>
                                <p className="text-xs text-violet-700/90 leading-relaxed">
                                    Continue without selecting a profile to create the
                                    booking with an empty job. You can add work items
                                    manually or import them from a Repco SmartQuote on
                                    the job card afterwards.
                                </p>
                            </div>
                        )
                    )}

                    {!loading && !error && filtered.length > 0 && (
                        <div className="space-y-1.5 max-h-48 overflow-y-auto pr-0.5">
                            {filtered.map(profile => {
                                const isSelected = selectedProfileUuid === profile.uuid;
                                const meta = [
                                    profile.make, profile.model, profile.vehicleYear,
                                    profile.mileageKm != null ? `${profile.mileageKm.toLocaleString()} km` : null,
                                ].filter(Boolean).join(' · ');

                                return (
                                    <button
                                        key={profile.uuid} type="button"
                                        onClick={() => onProfileSelect(profile.uuid)}
                                        className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all flex items-center gap-3
                                            ${isSelected
                                            ? 'border-violet-500 bg-violet-50 ring-1 ring-violet-400'
                                            : 'border-gray-200 bg-white hover:border-violet-300 hover:bg-violet-50/30'
                                        }`}
                                    >
                                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0
                                            ${isSelected ? 'border-violet-500' : 'border-gray-300'}`}>
                                            {isSelected && <div className="w-2 h-2 rounded-full bg-violet-500" />}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-semibold text-gray-900 truncate">{profile.name}</p>
                                            {meta && <p className="text-xs text-gray-500 truncate mt-0.5">{meta}</p>}
                                        </div>
                                        {profile.workItemCount != null && (
                                            <span className="shrink-0 text-xs px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 font-medium">
                                                {profile.workItemCount} item{profile.workItemCount !== 1 ? 's' : ''}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ==================== Service Profile Summary (Step 3) ====================

/**
 * Above this many work items the list scrolls inside the panel instead of growing.
 *
 * A starter logbook profile carries 37 items, each with its own parts breakdown, and
 * laid out in full it pushed the Date and Time fields — the only two things Step 4
 * actually requires — off the bottom of a modal that cannot itself be made taller.
 */
const SCROLL_ITEMS_ABOVE = 6;

/**
 * ServiceProfileSummary
 *
 * Fetches the full service profile by UUID and renders:
 *   • Each work item with its parts list and individual costs
 *   • Per-item labour cost and duration
 *   • Footer totals: total parts, total labour, total duration, grand total
 *
 * The fetch is deferred to mount so it only fires when the user reaches Step 3.
 * If the full profile was already in the Step 2 list we still do a detail fetch
 * because the list endpoint may not include the full nested parts data.
 */
export const ServiceProfileSummary = ({ profileUuid }) => {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);
    // Collapsed, and it RE-collapses on every profile change. The header already
    // carries the profile name and the item count, which is what somebody picking a
    // date needs; the breakdown is there to be checked, not to be read every time.
    // It used to expand on each selection, which put 37 items between Step 4 and its
    // Date field.
    const [collapsed, setCollapsed] = useState(true);

    useEffect(() => {
        if (!profileUuid) return;
        setLoading(true);
        setError(null);
        setCollapsed(true);
        getServiceProfileApi(profileUuid)
            .then(setProfile)
            .catch(() => setError('Could not load profile details.'))
            .finally(() => setLoading(false));
    }, [profileUuid]);

    if (loading) {
        return (
            <div className="flex items-center justify-center gap-2 py-8 text-gray-400 text-sm">
                <Loader2 size={16} className="animate-spin" /> Loading service profile…
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertCircle size={15} className="shrink-0" /> {error}
            </div>
        );
    }

    if (!profile) return null;

    const activeItems = (profile.workItems ?? []).filter(wi => wi.active !== false);

    // ── Aggregate totals ──
    const totals = activeItems.reduce((acc, wi) => {
        const labour = parseFloat(wi.labourCost ?? 0);
        const parts  = (wi.parts ?? []).reduce((s, p) => s + parseFloat(p.cost ?? 0), 0);
        const hours  = parseFloat(wi.estimatedHours ?? 0);
        return {
            labour:     acc.labour + labour,
            parts:      acc.parts  + parts,
            totalCost:  acc.totalCost + labour + parts,
            totalHours: acc.totalHours + hours,
        };
    }, { labour: 0, parts: 0, totalCost: 0, totalHours: 0 });

    // Convert total hours → h m display
    const totalHoursDisplay = (() => {
        const h = totals.totalHours;
        if (h === 0) return null;
        const wholeH = Math.floor(h);
        const mins   = Math.round((h - wholeH) * 60);
        if (wholeH === 0) return `${mins}m`;
        return mins > 0 ? `${wholeH}h ${mins}m` : `${wholeH}h`;
    })();

    return (
        <div className="rounded-xl border border-violet-200 bg-violet-50/40 overflow-hidden">

            {/* Profile header — click to expand/collapse the work items + totals.
                Wrapped in a button so the entire header row is the hit target;
                styling matches the prior <div> visually. */}
            <button
                type="button"
                onClick={() => setCollapsed(c => !c)}
                aria-expanded={!collapsed}
                aria-controls="service-profile-summary-body"
                className="w-full px-4 py-3 bg-violet-600 flex items-center gap-2 text-left hover:bg-violet-700 transition-colors"
            >
                <ClipboardList size={16} className="text-violet-100 shrink-0" />
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-white truncate">{profile.name}</p>
                    {(profile.make || profile.model || profile.mileageKm) && (
                        <p className="text-xs text-violet-200 truncate">
                            {[profile.make, profile.model, profile.vehicleYear,
                                profile.mileageKm != null ? `${profile.mileageKm.toLocaleString()} km` : null
                            ].filter(Boolean).join(' · ')}
                        </p>
                    )}
                </div>
                <span className="shrink-0 text-xs px-2 py-0.5 rounded-full bg-violet-500/60 text-violet-100 font-medium">
                    {activeItems.length} work item{activeItems.length !== 1 ? 's' : ''}
                </span>
                {collapsed
                    ? <ChevronDown size={16} className="text-violet-100 shrink-0" />
                    : <ChevronUp   size={16} className="text-violet-100 shrink-0" />}
            </button>

            {/* Collapsible body — work items + totals footer */}
            {!collapsed && (
                <div id="service-profile-summary-body">
                    {/* Work items */}
                    {activeItems.length === 0 ? (
                        <p className="px-4 py-4 text-sm text-gray-400 text-center">No work items defined for this profile.</p>
                    ) : (
                        <div className={`divide-y divide-violet-100${
                            activeItems.length > SCROLL_ITEMS_ABOVE
                                ? ' max-h-[22rem] overflow-y-auto overscroll-contain' : ''}`}>
                            {activeItems.map((wi, idx) => {
                                const wiLabour     = parseFloat(wi.labourCost ?? 0);
                                const wiPartsCost  = (wi.parts ?? []).reduce((s, p) => s + parseFloat(p.cost ?? 0), 0);
                                const wiTotal      = wiLabour + wiPartsCost;
                                const durationStr  = fmtHours(wi.estimatedHours);
                                const hasParts     = wi.parts && wi.parts.length > 0;

                                return (
                                    <div key={wi.uuid} className="px-4 py-3 space-y-2">
                                        {/* Work item header row */}
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex items-start gap-2 min-w-0">
                                                {/* Index badge */}
                                                <span className="shrink-0 mt-0.5 w-5 h-5 rounded-full bg-violet-100 text-violet-600
                                                                 text-xs font-bold flex items-center justify-center">
                                                    {idx + 1}
                                                </span>
                                                <p className="text-sm font-semibold text-gray-900 leading-snug">{wi.action}</p>
                                            </div>
                                            {wiTotal > 0 && (
                                                <span className="shrink-0 text-sm font-bold text-gray-900">
                                                    {fmt$(wiTotal)}
                                                </span>
                                            )}
                                        </div>

                                        {/* Cost + duration detail row */}
                                        <div className="ml-7 flex flex-wrap items-center gap-x-4 gap-y-1">
                                            {wiLabour > 0 && (
                                                <span className="flex items-center gap-1 text-xs text-gray-500">
                                                    <Wrench size={11} className="text-gray-400" />
                                                    Labour <span className="font-medium text-gray-700">{fmt$(wiLabour)}</span>
                                                </span>
                                            )}
                                            {wiPartsCost > 0 && (
                                                <span className="flex items-center gap-1 text-xs text-gray-500">
                                                    <Package size={11} className="text-gray-400" />
                                                    Parts <span className="font-medium text-gray-700">{fmt$(wiPartsCost)}</span>
                                                </span>
                                            )}
                                            {durationStr && (
                                                <span className="flex items-center gap-1 text-xs text-gray-500">
                                                    <Clock size={11} className="text-gray-400" />
                                                    <span className="font-medium text-gray-700">{durationStr}</span>
                                                </span>
                                            )}
                                        </div>

                                        {/* Parts breakdown */}
                                        {hasParts && (
                                            <div className="ml-7 space-y-1">
                                                {wi.parts.map(part => (
                                                    <div key={part.uuid}
                                                         className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg bg-white border border-violet-100">
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <Package size={12} className="text-violet-400 shrink-0" />
                                                            <span className="text-xs text-gray-700 font-medium truncate">
                                                                {part.name}
                                                            </span>
                                                            {part.manufacturerPartId && (
                                                                <span className="text-xs font-mono text-gray-400 shrink-0">
                                                                    #{part.manufacturerPartId}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {part.cost != null && (
                                                            <span className="shrink-0 text-xs font-semibold text-gray-700">
                                                                {fmt$(part.cost)}
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Totals footer */}
                    {activeItems.length > 0 && (
                        <div className="px-4 py-3 bg-violet-100/60 border-t border-violet-200 space-y-1.5">
                            {totals.labour > 0 && (
                                <div className="flex justify-between text-xs text-gray-600">
                                    <span className="flex items-center gap-1.5">
                                        <Wrench size={11} /> Total Labour
                                    </span>
                                    <span className="font-medium">{fmt$(totals.labour)}</span>
                                </div>
                            )}
                            {totals.parts > 0 && (
                                <div className="flex justify-between text-xs text-gray-600">
                                    <span className="flex items-center gap-1.5">
                                        <Package size={11} /> Total Parts
                                    </span>
                                    <span className="font-medium">{fmt$(totals.parts)}</span>
                                </div>
                            )}
                            {totalHoursDisplay && (
                                <div className="flex justify-between text-xs text-gray-600">
                                    <span className="flex items-center gap-1.5">
                                        <Clock size={11} /> Est. Duration
                                    </span>
                                    <span className="font-medium">{totalHoursDisplay}</span>
                                </div>
                            )}
                            {totals.totalCost > 0 && (
                                <div className="flex justify-between text-sm font-bold text-gray-900 pt-1.5 border-t border-violet-200">
                                    <span className="flex items-center gap-1.5">
                                        <DollarSign size={13} /> Estimated Total
                                    </span>
                                    <span>{fmt$(totals.totalCost)}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};


// ==================== Step 3 — Handover & Pickup Persons ====================

/**
 * HandoverPickupStep
 *
 * Optional contact details for the people physically handling the vehicle:
 *   - Handover person: drops the vehicle off at the workshop (may be a
 *                      family member, fleet driver, partner, etc — not
 *                      necessarily the booking customer)
 *   - Pickup person:   collects the vehicle after service is complete
 *
 * All four fields are optional. The user can hit "Continue" without filling
 * anything in — the wizard does not gate progression here. We provide a
 * "Skip — same as customer" button as a quick path for the common case
 * where the customer themselves will be doing both the drop-off and pickup.
 *
 * No client-side phone-format validation is applied: handover/pickup contacts
 * may be non-AU numbers (overseas family, for instance). Length is the only
 * cap (matches the backend @Size(max = 30) on mobile, 100 on name).
 */
const HandoverPickupStep = ({
                                formData,
                                onChange,
                                onBack,
                                onNext,
                                customerData,
                            }) => {
    const customerNameLabel = customerData?.fullName || customerData?.name || null;
    const customerPhoneLabel = customerData?.phone || null;

    // "Same as customer" quick-fill: copy the booking customer's name + phone
    // into both handover and pickup fields. Useful for the common case where
    // the customer is doing the drop-off and pickup themselves.
    const handleSameAsCustomer = (target /* 'handover' | 'pickup' | 'both' */) => {
        if (!customerData) return;
        const name = customerData.fullName || customerData.name || '';
        const phone = customerData.phone || '';
        if (target === 'handover' || target === 'both') {
            onChange({ target: { name: 'handoverPersonName', value: name } });
            onChange({ target: { name: 'handoverPersonMobile', value: phone } });
        }
        if (target === 'pickup' || target === 'both') {
            onChange({ target: { name: 'pickupPersonName', value: name } });
            onChange({ target: { name: 'pickupPersonMobile', value: phone } });
        }
    };

    const handleClear = (target /* 'handover' | 'pickup' */) => {
        if (target === 'handover') {
            onChange({ target: { name: 'handoverPersonName', value: '' } });
            onChange({ target: { name: 'handoverPersonMobile', value: '' } });
        } else {
            onChange({ target: { name: 'pickupPersonName', value: '' } });
            onChange({ target: { name: 'pickupPersonMobile', value: '' } });
        }
    };

    return (
        <div className="p-5 space-y-5 overflow-y-auto flex-1">

            {/* Optional banner */}
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2">
                <AlertCircle size={14} className="text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-800 leading-relaxed">
                    All fields on this step are optional. Skip if the customer is doing
                    the drop-off and pickup themselves, or fill in only what you know.
                </p>
            </div>

            {/* Quick action: same as customer for both */}
            {customerData && (customerNameLabel || customerPhoneLabel) && (
                <button
                    type="button"
                    onClick={() => handleSameAsCustomer('both')}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-violet-200 bg-violet-50 text-violet-700 text-sm font-medium hover:bg-violet-100 transition-colors"
                >
                    <Users size={14} />
                    Use customer details for both
                    {customerNameLabel && (
                        <span className="text-violet-500 font-normal">
                            ({customerNameLabel})
                        </span>
                    )}
                </button>
            )}

            {/* ── HANDOVER PERSON ── */}
            <div className="rounded-xl p-4 border bg-blue-50 border-blue-200">
                <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                        <User size={17} className="text-blue-600" />
                        <h3 className="font-semibold text-gray-900">Handover Person</h3>
                        <span className="text-xs text-gray-500 font-normal">(optional)</span>
                    </div>
                    <div className="flex items-center gap-1">
                        {customerData && (
                            <button
                                type="button"
                                onClick={() => handleSameAsCustomer('handover')}
                                className="text-xs text-violet-600 hover:text-violet-700 font-medium"
                            >
                                Same as customer
                            </button>
                        )}
                        {(formData.handoverPersonName || formData.handoverPersonMobile) && (
                            <>
                                <span className="text-gray-300">·</span>
                                <button
                                    type="button"
                                    onClick={() => handleClear('handover')}
                                    className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                                >
                                    Clear
                                </button>
                            </>
                        )}
                    </div>
                </div>
                <p className="text-xs text-gray-600 mb-3">
                    Who will physically drop the vehicle off at the workshop?
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label htmlFor="handoverPersonName"
                               className="block text-xs font-medium text-gray-600 mb-1">
                            Name
                        </label>
                        <input
                            id="handoverPersonName"
                            type="text"
                            name="handoverPersonName"
                            value={formData.handoverPersonName ?? ''}
                            onChange={onChange}
                            placeholder="e.g. John Smith"
                            maxLength={100}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-400"
                        />
                    </div>
                    <div>
                        <label htmlFor="handoverPersonMobile"
                               className="block text-xs font-medium text-gray-600 mb-1">
                            Mobile
                        </label>
                        <input
                            id="handoverPersonMobile"
                            type="tel"
                            name="handoverPersonMobile"
                            value={formData.handoverPersonMobile ?? ''}
                            onChange={onChange}
                            placeholder="e.g. 0412 345 678"
                            maxLength={30}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-400"
                        />
                    </div>
                </div>
            </div>

            {/* ── PICKUP PERSON ── */}
            <div className="rounded-xl p-4 border bg-emerald-50 border-emerald-200">
                <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                        <User size={17} className="text-emerald-600" />
                        <h3 className="font-semibold text-gray-900">Pickup Person</h3>
                        <span className="text-xs text-gray-500 font-normal">(optional)</span>
                    </div>
                    <div className="flex items-center gap-1">
                        {customerData && (
                            <button
                                type="button"
                                onClick={() => handleSameAsCustomer('pickup')}
                                className="text-xs text-violet-600 hover:text-violet-700 font-medium"
                            >
                                Same as customer
                            </button>
                        )}
                        {(formData.pickupPersonName || formData.pickupPersonMobile) && (
                            <>
                                <span className="text-gray-300">·</span>
                                <button
                                    type="button"
                                    onClick={() => handleClear('pickup')}
                                    className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                                >
                                    Clear
                                </button>
                            </>
                        )}
                    </div>
                </div>
                <p className="text-xs text-gray-600 mb-3">
                    Who will collect the vehicle after the service is complete?
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label htmlFor="pickupPersonName"
                               className="block text-xs font-medium text-gray-600 mb-1">
                            Name
                        </label>
                        <input
                            id="pickupPersonName"
                            type="text"
                            name="pickupPersonName"
                            value={formData.pickupPersonName ?? ''}
                            onChange={onChange}
                            placeholder="e.g. Jane Smith"
                            maxLength={100}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-400"
                        />
                    </div>
                    <div>
                        <label htmlFor="pickupPersonMobile"
                               className="block text-xs font-medium text-gray-600 mb-1">
                            Mobile
                        </label>
                        <input
                            id="pickupPersonMobile"
                            type="tel"
                            name="pickupPersonMobile"
                            value={formData.pickupPersonMobile ?? ''}
                            onChange={onChange}
                            placeholder="e.g. 0412 345 678"
                            maxLength={30}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-400"
                        />
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <div className="flex gap-3 pt-2">
                <button type="button" onClick={onBack}
                        className="flex items-center gap-1.5 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 font-medium text-sm hover:bg-gray-50 transition-colors">
                    <ChevronLeft size={16} /> Back
                </button>
                <button type="button" onClick={onNext}
                        className="flex-1 py-2.5 bg-violet-600 text-white rounded-xl font-medium text-sm hover:bg-violet-700 transition-colors flex items-center justify-center gap-1.5">
                    Continue <ChevronRight size={16} />
                </button>
            </div>
        </div>
    );
};

// ==================== Step 4 — Booking Details ====================

/**
 * BookingDetailsStep
 *
 * On Demand mode:   Date + Time + Service Type rows (each with its own
 *                   duration + description) + Odometer In (optional)
 * Service Profile:  Date + Time + Profile Summary panel + Odometer In
 *                   (optional) + Description / Notes
 *
 * Odometer In is optional in both modes — staff can leave it blank if a
 * reading isn't available at booking time and capture it later. On edit
 * the field is hidden entirely (the reading is locked to the job record).
 *
 * On-demand bookings get per-row description on each service type row so
 * a booking-level Description / Notes is suppressed for that mode (it
 * would just duplicate the per-row notes). Service Profile mode does not
 * have per-row notes (work items are pre-defined on the profile) so it
 * keeps the booking-level textarea.
 *
 * In edit mode the Loan Car panel is shown below the notes field. It receives
 * the loanCarIncidents and loanCarIncidentsLoading props that were fetched by
 * BookingsPage when the edit modal opened (mirroring the view modal behaviour).
 */
/**
 * Exported so the step can be rendered on its own in a test.
 *
 * ⚠️ It is a SEPARATE component from BookingFormModal and cannot reach its state — every
 * value it needs arrives as a prop. Referencing one that does not is a runtime
 * ReferenceError the build cannot see, which is exactly how selectedVehicleUuid shipped
 * broken: vite built it, and no test rendered this component.
 */
export const BookingDetailsStep = ({
                                formData, onChange, bookingServiceCategories, metaLoading,
                                isEdit, existingBooking, loading, error, onBack, onSubmit,
                                workItemMode, onWorkItemModeChange,
                                selectedServiceProfileUuid, onServiceProfileSelect,
                                // By Mileage needs the vehicle to look history up by, and
                                // the reading itself. Both live in BookingFormModal — this
                                // is a separate component and cannot reach its state.
                                selectedVehicleUuid, mileageOdometerKm, onMileageChange,
                                loanCarIncidents, loanCarIncidentsLoading,
                            }) => {
    const isServiceProfile = workItemMode === 'serviceProfile';
    const isMileage = workItemMode === 'mileage';
    /**
     * Modes where the work is DERIVED rather than typed — from a profile, or from the
     * odometer reading and the vehicle's history.
     *
     * ⚠️ The gates below used to read `isServiceProfile`, which made "not a profile" mean
     * "on demand". Adding a third mode broke that: By Mileage asked for service types it
     * does not use, and required them to be filled before the booking could be created —
     * so the button stayed grey with nothing to say why.
     */
    const isDerivedWork = isServiceProfile || isMileage;

    /** A mileage booking needs its reading before it can be created — it IS the input. */
    const hasMileageReading = mileageOdometerKm !== '' && mileageOdometerKm !== null
        && mileageOdometerKm !== undefined && Number(mileageOdometerKm) >= 0;

    // Per-row lock state — purely UI, not part of the submitted payload.
    // Once a row is locked the label/duration/description become read-only;
    // user can unlock by clicking the pencil to edit again.
    const [lockedTypes, setLockedTypes] = useState(() => new Set());
    const isLocked = (type) => lockedTypes.has(type);
    const toggleLocked = (type) => {
        setLockedTypes(prev => {
            const next = new Set(prev);
            if (next.has(type)) next.delete(type); else next.add(type);
            return next;
        });
    };
    const clearLock = (type) => {
        setLockedTypes(prev => {
            if (!prev.has(type)) return prev;
            const next = new Set(prev);
            next.delete(type);
            return next;
        });
    };

    // On-demand bookings can carry multiple service types, each with its own
    // estimated duration. At least one row is required and total duration
    // (sum of per-row minutes) is computed, not user-entered.
    const selectedServiceTypes = formData.serviceTypes ?? [];
    const totalDurationMinutes = selectedServiceTypes.reduce(
        (sum, row) => sum + (parseInt(row.durationMinutes) || 0), 0
    );

    const availableServiceTypes = bookingServiceCategories.filter(
        ct => !selectedServiceTypes.some(row => row.type === ct.value)
    );

    const addServiceType = (value) => {
        if (!value || selectedServiceTypes.some(row => row.type === value)) return;
        const next = [...selectedServiceTypes, { type: value, durationMinutes: '', description: '' }];
        onChange({ target: { name: 'serviceTypes', value: next } });
    };

    const updateServiceTypeDuration = (type, durationMinutes) => {
        const next = selectedServiceTypes.map(row =>
            row.type === type ? { ...row, durationMinutes } : row
        );
        onChange({ target: { name: 'serviceTypes', value: next } });
    };

    const updateServiceTypeDescription = (type, description) => {
        const next = selectedServiceTypes.map(row =>
            row.type === type ? { ...row, description } : row
        );
        onChange({ target: { name: 'serviceTypes', value: next } });
    };

    const removeServiceType = (type) => {
        const next = selectedServiceTypes.filter(row => row.type !== type);
        onChange({ target: { name: 'serviceTypes', value: next } });
        clearLock(type);
    };

    const labelForServiceType = (value) =>
        bookingServiceCategories.find(ct => ct.value === value)?.label
        ?? (typeof value === 'string' ? value.replace(/_/g, ' ') : '');

    // Odometer In is optional on both create and edit. The value still flows
    // through to the backend (parsed in handleFinalSubmit) when the user
    // chooses to enter it.
    const isValid = formData.scheduledDate
        // A mileage booking is valid on its reading alone; the work follows from it.
        && (isDerivedWork ? (!isMileage || hasMileageReading) : selectedServiceTypes.length > 0);

    return (
        <div className="p-5 space-y-5 overflow-y-auto flex-1">
            {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    <AlertCircle size={16} className="shrink-0" /> {error}
                </div>
            )}

            {/* ── Work-item mode selector (On Demand / Service Profile) ──
                Create-only. In edit mode the booking has already been
                materialised into a job — the originating profile (if any)
                is not re-pickable, and edits are re-saved via the on-demand
                path (POST /bookings → PUT /bookings/{uuid}). */}
            {!isEdit && (
                <ServiceProfileSelector
                    mode={workItemMode}
                    onModeChange={onWorkItemModeChange}
                    selectedProfileUuid={selectedServiceProfileUuid}
                    onProfileSelect={onServiceProfileSelect}
                    vehicleUuid={selectedVehicleUuid}
                    mileageOdometerKm={mileageOdometerKm}
                    onMileageChange={onMileageChange}
                />
            )}

            {/* ── Date & Time — always shown ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date <span className="text-red-500">*</span>
                    </label>
                    <DatePickerField
                        name="scheduledDate"
                        value={formData.scheduledDate}
                        onChange={onChange}
                        required
                        ariaLabel="Scheduled date"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                    <select name="scheduledTime" value={formData.scheduledTime} onChange={onChange}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm">
                        <option value="">No specific time</option>
                        {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>
            </div>

            {/* ── On Demand controls: Service Types (multi) — each with its own duration ── */}
            {!isDerivedWork && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Service Types <span className="text-red-500">*</span>
                        {selectedServiceTypes.length > 0 && (
                            <span className="ml-2 text-xs font-normal text-gray-500">
                                {selectedServiceTypes.length} selected
                                {totalDurationMinutes > 0 && ` · total ${totalDurationMinutes} min`}
                            </span>
                        )}
                    </label>

                    {/* Selected service-type rows: label + per-row DurationInput + lock + remove,
                        with a per-row description textarea underneath */}
                    {selectedServiceTypes.length > 0 && (
                        <div className="space-y-1.5 mb-2">
                            {selectedServiceTypes.map(row => {
                                const locked = isLocked(row.type);
                                return (
                                    <div key={row.type}
                                         className={`px-2.5 py-2 border rounded-lg space-y-1.5 transition-colors ${
                                             locked
                                                 ? 'bg-gray-50 border-gray-200'
                                                 : 'bg-violet-50 border-violet-200'
                                         }`}>
                                        <div className="flex items-center gap-2">
                                            <span className={`flex-1 min-w-0 text-sm font-medium truncate ${
                                                locked ? 'text-gray-700' : 'text-violet-900'
                                            }`}>
                                                {labelForServiceType(row.type)}
                                            </span>
                                            <DurationInput
                                                compact
                                                disabled={locked}
                                                value={row.durationMinutes}
                                                onChange={(mins) => updateServiceTypeDuration(row.type, mins)}
                                            />
                                            <button type="button"
                                                    onClick={() => toggleLocked(row.type)}
                                                    title={locked ? 'Edit this service' : 'Mark as done (lock row)'}
                                                    aria-label={locked ? 'Edit this service' : 'Mark as done'}
                                                    className={`p-1 rounded shrink-0 ${
                                                        locked
                                                            ? 'text-gray-500 hover:bg-gray-200'
                                                            : 'text-emerald-600 hover:bg-emerald-100'
                                                    }`}>
                                                {locked ? <Pencil size={14} /> : <Check size={14} />}
                                            </button>
                                            <button type="button"
                                                    onClick={() => removeServiceType(row.type)}
                                                    className={`p-1 rounded shrink-0 ${
                                                        locked
                                                            ? 'text-gray-400 hover:bg-gray-200'
                                                            : 'text-violet-600 hover:bg-violet-100'
                                                    }`}
                                                    aria-label={`Remove ${labelForServiceType(row.type)}`}>
                                                <X size={14} />
                                            </button>
                                        </div>
                                        <textarea
                                            value={row.description ?? ''}
                                            onChange={(e) => updateServiceTypeDescription(row.type, e.target.value)}
                                            disabled={locked}
                                            placeholder="Description / notes for this service (optional)"
                                            rows={2}
                                            className={`w-full px-2 py-1.5 text-xs border rounded-md
                                                        focus:ring-1 focus:ring-violet-500 focus:border-violet-500
                                                        placeholder-gray-400 resize-y
                                                        ${locked
                                                ? 'bg-gray-100 border-gray-200 text-gray-600 cursor-not-allowed'
                                                : 'bg-white border-violet-200'}`}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Add picker — resets after each pick */}
                    <select
                        value=""
                        onChange={(e) => { addServiceType(e.target.value); e.target.value = ''; }}
                        disabled={metaLoading || availableServiceTypes.length === 0}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm disabled:opacity-60">
                        {metaLoading
                            ? <option value="">Loading…</option>
                            : <>
                                <option value="" disabled>
                                    {availableServiceTypes.length === 0
                                        ? 'All service types added'
                                        : selectedServiceTypes.length === 0
                                            ? 'Select a service type to add'
                                            : '+ Add another service type'}
                                </option>
                                {availableServiceTypes.map(ct => (
                                    <option key={ct.value} value={ct.value}>{ct.label}</option>
                                ))}
                            </>
                        }
                    </select>

                    <p className="text-xs text-gray-400 mt-1.5">
                        Set a duration per service (d / h / m). Total estimated duration is the sum of all rows. 1 day = 8 hours.
                    </p>
                </div>
            )}

            {/* ── Service Profile summary ── */}
            {isServiceProfile && selectedServiceProfileUuid && (
                <ServiceProfileSummary profileUuid={selectedServiceProfileUuid} />
            )}

            {/* ── Odometer In — optional, hidden on edit ──
                ⚠️ And hidden in By Mileage mode, where the reading has already been
                asked for at the top and IS this number. Two fields for one value
                invites two different answers, and the pair would then disagree about
                the same vehicle on the same day. The reading is sent as odometerIn on
                submit, so the job still gets it. */}
            {!isEdit && !isMileage && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        <span className="flex items-center gap-1.5">
                            <Gauge size={14} /> Odometer In
                        </span>
                    </label>
                    <div className="relative">
                        <input
                            type="number"
                            name="odometerIn"
                            value={formData.odometerIn}
                            onChange={onChange}
                            placeholder="e.g. 85000"
                            min="0"
                            max="9999999"
                            className="w-full pl-3 pr-12 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm
                                [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-medium pointer-events-none">
                            km
                        </span>
                    </div>
                </div>
            )}

            {/* ── Description / Notes — Service Profile only ─────────────────
                On Demand mode has per-row description fields on each service
                type entry, so a booking-level "Description / Notes" textarea
                here would just create two places to write the same thing.
                Service Profile mode does not have per-row notes (work items
                are pre-defined on the profile), so it keeps this field. */}
            {isDerivedWork && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description / Notes
                    </label>
                    <textarea name="description" value={formData.description} onChange={onChange}
                              rows={3} placeholder="Customer's description of the issue or requested service…"
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm resize-none" />
                </div>
            )}

            {/* ── Loan Car — edit mode only ── */}
            {isEdit && existingBooking?.uuid && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <LoanCarAllocationPanel
                        booking={existingBooking}
                        loanCarIncidents={loanCarIncidents}
                        loanCarIncidentsLoading={loanCarIncidentsLoading}
                    />
                </div>
            )}

            {/* Navigation */}
            <div className="flex gap-3 pt-2">
                <button type="button" onClick={onBack}
                        className="flex items-center gap-1.5 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 font-medium text-sm hover:bg-gray-50 transition-colors">
                    <ChevronLeft size={16} /> Back
                </button>
                <button type="button" onClick={onSubmit}
                        disabled={loading || !isValid}
                        className="flex-1 py-2.5 bg-violet-600 text-white rounded-xl font-medium text-sm hover:bg-violet-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                    {loading && <Loader2 size={18} className="animate-spin" />}
                    {isEdit ? 'Update Booking' : 'Create Booking'}
                </button>
            </div>
        </div>
    );
};

// ==================== Main Modal ====================

const BookingFormModal = ({
                              isEdit                  = false,
                              existingBooking         = null,
                              initialData             = null,
                              bookingServiceCategories           = [],
                              metaLoading             = false,
                              loading                 = false,
                              error                   = null,
                              onSubmit,                              // called for ad-hoc (on-demand) bookings → POST /bookings
                              onSubmitServiceProfile  = onSubmit,    // called for service-profile bookings → POST /bookings/from-service-profile
                              // called for By Mileage bookings → POST /bookings/from-mileage.
                              // Defaults to the ad-hoc submit so a caller that has not
                              // wired it up creates an ordinary booking rather than
                              // silently dropping the one the user filled in.
                              onSubmitMileage         = onSubmit,
                              onClose,
                              loanCarIncidents        = [],          // incidents fetched by BookingsPage when edit modal opens
                              loanCarIncidentsLoading = false,
                              // Phone mode: fill the viewport instead of floating as a draggable
                              // card, and drop the drag affordance. Used by MobileBookingPage so
                              // the phone gets the SAME wizard — same validation, same ownership
                              // check, same two submit endpoints — rather than a reduced
                              // reimplementation that would drift from this one.
                              fullScreen              = false,
                          }) => {
    const [step, setStep] = useState(isEdit ? 2 : 1);
    const [foundCustomer, setFoundCustomer]         = useState(null);
    const [selectedVehicleUuid, setSelectedVehicleUuid] = useState(null);
    /** The odometer reading a By Mileage booking is worked out from. */
    const [mileageOdometerKm, setMileageOdometerKm] = useState('');
    const [addingNewVehicle, setAddingNewVehicle]    = useState(false);
    const [editingVehicleUuid, setEditingVehicleUuid] = useState(null);

    // Desktop-only Escape to close. Mounted while the modal is rendered;
    // the shared stack ensures the VehicleHistorySlideOut child (when open)
    // captures Escape first, only closing this modal once the slide-out is
    // dismissed.
    useEscapeToClose(true, onClose);

    // ── Draggable modal positioning ──────────────────────────────────────
    // Modal is non-modal (no backdrop) and can be moved around via the header
    // so the user can see/click calendar slots behind it. Position is stored
    // as { top, left } in px; null means "use default centered position".
    const [modalPos, setModalPos] = useState(null);
    const modalRef = useRef(null);
    const dragStateRef = useRef(null); // { offsetX, offsetY } during a drag

    const handleHeaderMouseDown = (e) => {
        // Nothing to drag in full-screen mode — the card already fills the
        // viewport, and a phone has no mouse to drag it with anyway.
        if (fullScreen) return;
        // Don't start a drag if the user clicked an interactive element (e.g. close button)
        if (e.target.closest('button')) return;
        const rect = modalRef.current?.getBoundingClientRect();
        if (!rect) return;
        dragStateRef.current = {
            offsetX: e.clientX - rect.left,
            offsetY: e.clientY - rect.top,
        };
        // Seed position from current rect so the first move doesn't jump
        setModalPos({ top: rect.top, left: rect.left });
        e.preventDefault();
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!dragStateRef.current) return;
            const { offsetX, offsetY } = dragStateRef.current;
            const rect = modalRef.current?.getBoundingClientRect();
            const w = rect?.width ?? 0;
            const h = rect?.height ?? 0;
            // Clamp so the modal stays at least partially visible on screen
            const maxLeft = window.innerWidth - 80;
            const maxTop = window.innerHeight - 60;
            const nextLeft = Math.min(Math.max(e.clientX - offsetX, -(w - 80)), maxLeft);
            const nextTop = Math.min(Math.max(e.clientY - offsetY, 0), maxTop);
            setModalPos({ top: nextTop, left: nextLeft });
        };
        const handleMouseUp = () => {
            dragStateRef.current = null;
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    // Work item mode — drives which API path the booking takes on submit:
    //   'onDemand'       → POST /bookings (ad-hoc, with serviceTypes[])
    //   'serviceProfile' → POST /bookings/from-service-profile (with serviceProfileUuid)
    //
    // Edit mode always pins to 'onDemand'. Bookings that were originally
    // created from a service profile get re-saved as plain on-demand
    // bookings — the backend has already materialised the work items onto
    // the resulting job, so there is no profile to "re-pick" on edit.
    const [workItemMode, setWorkItemMode] = useState('onDemand');
    const [selectedServiceProfileUuid, setSelectedServiceProfileUuid] = useState(null);

    // Switching modes clears the picked profile so a stale UUID can't leak
    // into an on-demand submit (and isValid recomputes correctly).
    const handleWorkItemModeChange = (nextMode) => {
        if (nextMode === workItemMode) return;
        setWorkItemMode(nextMode);
        // ⚠️ Clear whichever mode is being left. The submit branch reads BOTH the mode
        // and its field, so a stale profile uuid or odometer reading left behind by a
        // change of mind would decide the booking rather than what is on screen.
        if (nextMode !== 'serviceProfile') {
            setSelectedServiceProfileUuid(null);
        }
        if (nextMode !== 'mileage') {
            setMileageOdometerKm('');
        }
    };

    const onMileageChange = (value) => setMileageOdometerKm(value);

    const [formData, setFormData] = useState(() => {
        // Hydrate serviceTypes from existing booking. Two shapes are supported:
        //   1. New:    existingBooking.serviceTypes = [{ serviceName, durationMinutes, description }]
        //   2. Legacy: existingBooking.serviceType (string) + estimatedDurationMinutes (number)
        // The backend DTO sends { serviceName } not { type } — normalise here.
        let initialServiceTypes = [];
        if (Array.isArray(existingBooking?.serviceTypes) && existingBooking.serviceTypes.length > 0) {
            initialServiceTypes = existingBooking.serviceTypes.map(row => ({
                type: row.serviceName ?? row.type,
                durationMinutes: row.durationMinutes ?? '',
                description: row.description ?? '',
            }));
        } else if (existingBooking?.serviceType) {
            initialServiceTypes = [{
                type: existingBooking.serviceType,
                durationMinutes: existingBooking.estimatedDurationMinutes ?? '',
                description: '',
            }];
        }

        return {
            customerType:         'INDIVIDUAL',  // 'INDIVIDUAL' | 'BUSINESS'
            // Seeded from a phone pre-booking when one is being converted. These are raw
            // speech-recognition values — deliberately EDITABLE and never trusted, which is
            // the whole reason the bot no longer books directly. Blank for a normal create.
            customerName:         initialData?.customerName  ?? '',
            customerBusinessName: '',            // used when customerType === BUSINESS
            customerPhone:        initialData?.customerPhone ?? '',
            customerEmail: '',
            vehicleRego:      initialData?.vehicleRego ?? '',
            vehicleRegoState: 'VIC',
            vehicleMake:      '',
            vehicleModel:     '',
            vehicleYear:      '',
            vehicleVin:          '',
            vehicleEngineNumber: '',
            vehicleTransmission: '',           // 'MANUAL' | 'AUTOMATIC' | '' (unset)
            scheduledDate: existingBooking?.scheduledDate ?? initialData?.date ?? '',
            scheduledTime: existingBooking?.scheduledTime
                ? existingBooking.scheduledTime.substring(0, 5)
                : (initialData?.time ?? ''),
            serviceTypes: initialServiceTypes,    // [{ type, durationMinutes }]
            description:  existingBooking?.description ?? '',
            odometerIn:   '',                     // optional on create; not shown on edit
            // Optional handover / pickup persons — hydrated from existing
            // booking on edit, otherwise blank. Empty string is the unset
            // state; the submit handler converts blanks to null on the wire.
            handoverPersonName:   existingBooking?.handoverPersonName   ?? '',
            handoverPersonMobile: existingBooking?.handoverPersonMobile ?? '',
            pickupPersonName:     existingBooking?.pickupPersonName     ?? '',
            pickupPersonMobile:   existingBooking?.pickupPersonMobile   ?? '',
        };
    });

    // No auto-default for service types — with multi-select the user explicitly
    // adds each one they want. Empty array is a valid initial state.

    useEffect(() => {
        if (isEdit && existingBooking) {
            setFoundCustomer({
                uuid:     existingBooking.customerUuid,
                fullName: existingBooking.customerName,
                phone:    existingBooking.customerPhone,
                email:    existingBooking.customerEmail,
                // Seed with the booking's vehicle so CustomerVehicleStep renders
                // it in the radio list and marks it selected. The booking DTO
                // gives us vehicleDescription ("2000 Honda Civic") and vehicleRego
                // — that's all we have, and it's all the edit view needs.
                // To change vehicle, the user cancels the booking and re-books.
                vehicles: existingBooking.vehicleUuid ? [{
                    uuid:        existingBooking.vehicleUuid,
                    displayName: existingBooking.vehicleDescription || existingBooking.vehicleRego || 'Vehicle',
                    regoNumber:  existingBooking.vehicleRego,
                    regoState:   null,
                    colour:      null,
                }] : [],
            });
            setSelectedVehicleUuid(existingBooking.vehicleUuid);
        }
    }, [isEdit, existingBooking]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        let processed = value;
        if (name === 'vehicleRego')         processed = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (name === 'vehicleVin')          processed = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (name === 'vehicleEngineNumber') processed = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (name === 'customerPhone') processed = value.replace(/[^\d\s\-()]/g, '');
        // Strip non-digits from odometer, clamp to zero minimum
        if (name === 'odometerIn') {
            const num = parseInt(value);
            processed = isNaN(num) || num < 0 ? '' : String(num);
        }
        setFormData(prev => ({ ...prev, [name]: processed }));
    };

    const handleCustomerFound = (customer, searchedRego = null) => {
        setFoundCustomer(customer);

        // Vehicle pre-selection priority:
        //   1. If a rego was used in the lookup search, find the customer's
        //      vehicle whose regoNumber matches (case-insensitive substring,
        //      mirroring the backend's LIKE %x% match). This saves staff
        //      having to find it themselves when the customer has many
        //      vehicles. If multiple vehicles match the same partial, take
        //      the first; staff can change it on step 2 if needed.
        //   2. Otherwise, if the customer has exactly one vehicle, select it.
        //   3. Else leave the selection blank for staff to choose.
        const vehicles = customer.vehicles ?? [];
        let preselected = null;
        if (searchedRego && vehicles.length > 0) {
            const needle = searchedRego.toUpperCase().replace(/[\s\-]/g, '');
            preselected = vehicles.find(v =>
                (v.regoNumber ?? '').toUpperCase().replace(/[\s\-]/g, '').includes(needle)
            );
        }
        if (!preselected && vehicles.length === 1) {
            preselected = vehicles[0];
        }
        setSelectedVehicleUuid(preselected ? preselected.uuid : null);

        setStep(2);
    };

    const handleSkipLookup = ({ prefill = {} }) => {
        setFoundCustomer(null);
        setSelectedVehicleUuid(null);
        // If the user typed a rego in the lookup step, flip into "add new
        // vehicle" mode so the rego field is visible and pre-filled. Otherwise
        // start clean (no foundCustomer means there's no vehicle list anyway).
        setAddingNewVehicle(!!prefill.vehicleRego);
        // Carry over every field the user typed in the lookup step so they
        // don't have to re-type.
        setFormData(prev => ({
            ...prev,
            ...(prefill.customerPhone && { customerPhone: prefill.customerPhone }),
            ...(prefill.customerEmail && { customerEmail: prefill.customerEmail }),
            ...(prefill.customerName  && { customerName:  prefill.customerName  }),
            ...(prefill.vehicleRego   && { vehicleRego:   prefill.vehicleRego   }),
        }));
        setStep(2);
    };

    const handleFinalSubmit = () => {
        // Both payloads are built by shared helpers — the quote form sends the same two
        // shapes to the same server-side resolvers, and two forms disagreeing about
        // them is how one of them starts creating duplicate customers.
        const customerPayload = buildCustomerPayload({ foundCustomer, formData });
        const vehiclePayload  = buildVehiclePayload({ selectedVehicleUuid, addingNewVehicle, formData });

        // odometerIn is optional; parse to int when supplied, null otherwise
        const odometerIn = formData.odometerIn !== '' && formData.odometerIn != null
            ? parseInt(formData.odometerIn)
            : null;

        // Optional handover / pickup person details — empty string becomes
        // null on the wire so the backend persists null rather than blanks.
        // The backend also runs trim/normalisation, but we send clean values
        // here as a courtesy and to keep payloads identical to what the user
        // sees in the form.
        const blank = (s) => (s == null ? null : (s.trim() === '' ? null : s.trim()));
        const handoverPickupPayload = {
            handoverPersonName:   blank(formData.handoverPersonName),
            handoverPersonMobile: blank(formData.handoverPersonMobile),
            pickupPersonName:     blank(formData.pickupPersonName),
            pickupPersonMobile:   blank(formData.pickupPersonMobile),
        };

        if (workItemMode === 'mileage' && mileageOdometerKm !== '' && mileageOdometerKm !== null) {
            // ── Mileage path ──
            // POST /bookings/from-mileage. The work is not sent: the server recomputes
            // it from this reading and the vehicle's history when the job is created, so
            // that what is done is always traceable to a number rather than to a list
            // this screen happened to be showing.
            onSubmitMileage({
                scheduledDate:     formData.scheduledDate,
                scheduledTime:     formData.scheduledTime || null,
                mileageOdometerKm: Number(mileageOdometerKm),
                description:       formData.description   || null,
                customer:          customerPayload,
                vehicle:           vehiclePayload,
                // ⚠️ The SAME reading. The Odometer In field is hidden in this mode
                // because it would be a second box for one number; the job still needs
                // the value, so it is sent from the one the user actually typed.
                odometerIn:        Number(mileageOdometerKm),
                ...handoverPickupPayload,
            });
        } else if (workItemMode === 'serviceProfile' && selectedServiceProfileUuid) {
            // ── Service-profile path ──
            // POST /bookings/from-service-profile
            // serviceType and estimatedDurationMinutes are intentionally omitted —
            // the backend derives them from the profile's work items.
            onSubmitServiceProfile({
                scheduledDate:      formData.scheduledDate,
                scheduledTime:      formData.scheduledTime || null,
                serviceProfileUuid: selectedServiceProfileUuid,
                description:        formData.description   || null,
                customer:           customerPayload,
                vehicle:            vehiclePayload,
                odometerIn,
                ...handoverPickupPayload,
            });
        } else {
            // ── Ad-hoc (on-demand) path ──
            // POST /bookings
            //
            // Also covers the "Service Profile mode but no profile selected"
            // case — when the workshop has no profiles set up yet we let the
            // user proceed and create the booking with an empty job. Work
            // items can be added manually or imported from a Repco SmartQuote
            // on the Job Detail Modal afterwards (gated to CREATED / QUOTED).
            // In that case serviceTypes is an empty array and
            // estimatedDurationMinutes is null — the backend must accept this.
            //
            // serviceTypes: [{ type: string, durationMinutes: number|null, description: string|null }]
            // estimatedDurationMinutes:      sum of all per-row durations (computed; sent for
            //                                backend convenience so it doesn't need to re-sum
            //                                on read paths). Send null when nothing entered.
            const serviceTypesPayload = (formData.serviceTypes ?? []).map(row => ({
                type: row.type,
                durationMinutes: row.durationMinutes !== '' && row.durationMinutes != null
                    ? parseInt(row.durationMinutes)
                    : null,
                description: row.description?.trim() ? row.description.trim() : null,
            }));
            const totalDuration = serviceTypesPayload.reduce(
                (sum, row) => sum + (row.durationMinutes || 0), 0
            );

            onSubmit({
                scheduledDate:            formData.scheduledDate,
                scheduledTime:            formData.scheduledTime || null,
                serviceTypes:             serviceTypesPayload,
                description:              formData.description || null,
                estimatedDurationMinutes: totalDuration > 0 ? totalDuration : null,
                customer:  customerPayload,
                vehicle:   vehiclePayload,
                odometerIn,
                ...handoverPickupPayload,
            });
        }
    };

    return (
        <div
            ref={modalRef}
            className={
                fullScreen
                    // ABSOLUTE, not fixed. The caller (MobileBookingPage) gives this a
                    // positioned parent that is the phone shell's content area, so the
                    // wizard fills the screen but stops above the bottom tab bar. With
                    // `fixed inset-0` it painted over the tab bar instead, and since
                    // this wizard has no "back to the list" destination on a phone,
                    // that left the user with no way out of the Booking tab at all.
                    // Safe-area insets are the shell's job for the same reason.
                    ? 'absolute inset-0 bg-white flex flex-col z-40'
                    : 'fixed bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl border border-gray-200 z-50'
            }
            style={
                fullScreen
                    ? undefined
                    : modalPos
                        ? { top: `${modalPos.top}px`, left: `${modalPos.left}px` }
                        : { top: '5vh', left: '50%', transform: 'translateX(-50%)' }
            }
        >

            <div
                onMouseDown={handleHeaderMouseDown}
                className={`flex items-center justify-between px-4 pt-4 pb-0 shrink-0 select-none ${
                    fullScreen ? '' : 'cursor-move'
                }`}
            >
                <div className="flex items-center gap-2">
                    {!fullScreen && <GripVertical size={16} className="text-gray-400" />}
                    <h2 className="text-lg font-bold text-gray-900">
                        {isEdit ? 'Edit Booking' : 'New Booking'}
                    </h2>
                </div>
                <button type="button" onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-500">
                    <X size={20} />
                </button>
            </div>

            <StepIndicator currentStep={step} isEdit={isEdit} />

            {step === 1 && (
                <CustomerLookupStep onFound={handleCustomerFound} onSkip={handleSkipLookup} prefill={initialData} />
            )}

            {step === 2 && (
                <CustomerVehicleStep
                    customerData={foundCustomer}
                    vehicles={foundCustomer?.vehicles ?? []}
                    selectedVehicleUuid={selectedVehicleUuid}
                    setSelectedVehicleUuid={setSelectedVehicleUuid}
                    addingNewVehicle={addingNewVehicle}
                    setAddingNewVehicle={setAddingNewVehicle}
                    formData={formData}
                    onChange={handleChange}
                    isEdit={isEdit}
                    onBack={() => setStep(1)}
                    onNext={() => setStep(3)}
                    editingVehicleUuid={editingVehicleUuid}
                    setEditingVehicleUuid={setEditingVehicleUuid}
                    anchorRef={modalRef}
                    onHeaderMouseDown={handleHeaderMouseDown}
                />
            )}

            {step === 3 && (
                <HandoverPickupStep
                    formData={formData}
                    onChange={handleChange}
                    customerData={foundCustomer}
                    onBack={() => setStep(2)}
                    onNext={() => setStep(4)}
                />
            )}

            {step === 4 && (
                <BookingDetailsStep
                    formData={formData}
                    onChange={handleChange}
                    bookingServiceCategories={bookingServiceCategories}
                    metaLoading={metaLoading}
                    isEdit={isEdit}
                    existingBooking={existingBooking}
                    loading={loading}
                    error={error}
                    onBack={() => setStep(3)}
                    onSubmit={handleFinalSubmit}
                    workItemMode={workItemMode}
                    onWorkItemModeChange={handleWorkItemModeChange}
                    selectedServiceProfileUuid={selectedServiceProfileUuid}
                    onServiceProfileSelect={setSelectedServiceProfileUuid}
                    selectedVehicleUuid={selectedVehicleUuid}
                    mileageOdometerKm={mileageOdometerKm}
                    onMileageChange={onMileageChange}
                    loanCarIncidents={loanCarIncidents}
                    loanCarIncidentsLoading={loanCarIncidentsLoading}
                />
            )}
        </div>
    );
};

export default BookingFormModal;